import { createDebugger } from '~/scripts/utils/debug.js';

const debug = createDebugger('klaviyo', false);

debug.log('helper loaded');

/**
 * Fire a Klaviyo onsite event. Falls back to a console warning when Klaviyo
 * isn't loaded yet (e.g. local dev) so the payload is still visible in devtools.
 */
export function trackEvent(eventName, eventData = {}) {
    if (window.klaviyo?.track) {
        return window.klaviyo.track(eventName, eventData);
    }
    debug.warn('klaviyo not loaded, would have tracked:', { eventName, eventData });
}

/**
 * Identify the current visitor on Klaviyo. Call this before `trackEvent` on
 * any form submit that captures an email, otherwise the event lands on an
 * anonymous cookie profile that never gets merged with the customer.
 */
export function identify(traits = {}) {
    if (window.klaviyo?.identify) {
        return window.klaviyo.identify(traits);
    }
    debug.warn('klaviyo not loaded, would have identified:', traits);
}

function initViewedCategory() {
    const ctx = window.__klaviyoContext;
    if (!ctx || ctx.page_type !== 'collection') return;

    trackEvent('Viewed Category', {
        category_name: ctx.category_name,
        category_type: ctx.category_type,
        url: ctx.url,
        timestamp: new Date().toISOString()
    });
}

/* ------------------------------------------------------------------------ *
 * Form events – wiring contract                                            *
 * ------------------------------------------------------------------------ *
 * The four form-based events from the Modern Classics CTO spec
 * (Requested Sample, Submitted Advice Form, Booked Showroom Visit,
 * Submitted Project Inquiry) have no form sections in the theme yet.
 *
 * When each form is built, add `data-klaviyo-event="<Event Name>"` to the
 * <form> tag and the global submit listener below will fire the event with
 * every named field in the form as the payload. Add `data-klaviyo-static`
 * for fields that aren't in the form (e.g. `category_type:wallpaper`).
 *
 * Example for the advice form:
 *   <form data-klaviyo-event="Submitted Advice Form"
 *         data-klaviyo-static="category_type:wallpaper">
 *     <input type="email" name="email" required>
 *     <input name="room_context">
 *     <textarea name="free_text"></textarea>
 *     <input type="checkbox" name="showroom_interest" value="true">
 *   </form>
 *
 * If a form is built as a Klaviyo native embed or a third-party widget
 * (e.g. Cowlendar for showroom bookings), it won't go through this listener
 * and needs a dedicated hook, see open questions in the handover doc.
 * ------------------------------------------------------------------------ */

const SHOPIFY_FORM_NOISE = new Set(['form_type', 'utf8', 'authenticity_token']);

function parseStaticFields(raw) {
    if (!raw) return {};
    const out = {};
    for (const pair of raw.split(',')) {
        const idx = pair.indexOf(':');
        if (idx === -1) continue;
        const k = pair.slice(0, idx).trim();
        const v = pair.slice(idx + 1).trim();
        if (k) out[k] = v;
    }
    return out;
}

function initFormEvents() {
    document.addEventListener(
        'submit',
        (event) => {
            const form = event.target;
            if (!(form instanceof HTMLFormElement)) return;
            const eventName = form.dataset.klaviyoEvent;
            if (!eventName) return;

            const payload = parseStaticFields(form.dataset.klaviyoStatic);

            for (const [key, value] of new FormData(form).entries()) {
                if (SHOPIFY_FORM_NOISE.has(key) || key.startsWith('_')) continue;
                if (payload[key] === undefined) {
                    payload[key] = typeof value === 'string' ? value : value.name;
                }
            }

            const emailInput = form.querySelector('input[type="email"]');
            if (emailInput?.value) {
                identify({ $email: emailInput.value });
            }

            trackEvent(eventName, payload);
        },
        true
    );
}

function init() {
    initViewedCategory();
    initFormEvents();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
